import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { transactionAPI, formatCurrency } from '../services/api';
import { FileText, ArrowUpDown, Filter, X, Search } from 'lucide-react';
import { formatDateDisplay } from '../lib/dateUtils';

const TYPE_LABELS = {
  invoice: 'Invoice', advance: 'Advance', receipt: 'Receipt',
};
const TYPE_COLORS = {
  invoice: 'bg-blue-50 text-blue-700', advance: 'bg-purple-50 text-purple-700', receipt: 'bg-emerald-50 text-emerald-700',
};
const VOUCHER_LABELS = {
  sales: 'Sales', purchase: 'Purchase', advance_received: 'Adv Received',
  advance_paid: 'Adv Paid', transport: 'Transport',
};

export default function TransactionsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ transactions: [], total: 0, summary: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: '', search: '', date_from: '', date_to: '', page: 1 });

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 30, ...filters };
    Object.keys(params).forEach(k => !params[k] && delete params[k]);
    transactionAPI.list(params).then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const s = data.summary || {};

  return (
    <div data-testid="transactions-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">All Transactions</h1>
          <p className="text-sm text-slate-500 mt-1">Unified view of invoices, advances, and receipts</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { l: 'Invoices', v: s.total_invoices || 0, c: 'text-blue-700' },
          { l: 'Advances', v: s.total_advances || 0, c: 'text-purple-700' },
          { l: 'Receipts', v: s.total_receipts || 0, c: 'text-emerald-700' },
          { l: 'Sales', v: `Rs.${formatCurrency(s.total_sales || 0)}`, c: 'text-emerald-700' },
          { l: 'Purchases', v: `Rs.${formatCurrency(s.total_purchases || 0)}`, c: 'text-rose-700' },
          { l: 'GST', v: `Rs.${formatCurrency(s.total_gst || 0)}`, c: 'text-orange-700' },
          { l: 'TDS', v: `Rs.${formatCurrency(s.total_tds || 0)}`, c: 'text-indigo-700' },
        ].map((m, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-md p-3">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{m.l}</p>
            <p className={`text-base font-bold font-mono mt-1 ${m.c}`}>{m.v}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 glass-card rounded-xl p-2.5">
        <Search className="w-3.5 h-3.5 text-purple-400 shrink-0" />
        <input type="text" placeholder="Search invoice, party..." value={filters.search}
          onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))}
          className="form-input w-44 py-2 text-xs" data-testid="tx-search" />
        <select value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value, page: 1 }))}
          className="form-input w-32 py-2 text-xs" data-testid="tx-type-filter">
          <option value="">All Types</option>
          <option value="invoice">Invoice</option>
          <option value="advance">Advance</option>
          <option value="receipt">Receipt</option>
        </select>
        <input type="date" value={filters.date_from}
          onChange={e => setFilters(f => ({ ...f, date_from: e.target.value, page: 1 }))}
          className="form-input w-32 py-2 text-xs" data-testid="tx-date-from" />
        <input type="date" value={filters.date_to}
          onChange={e => setFilters(f => ({ ...f, date_to: e.target.value, page: 1 }))}
          className="form-input w-32 py-2 text-xs" data-testid="tx-date-to" />
        {(filters.search || filters.type || filters.date_from || filters.date_to) && (
          <button onClick={() => setFilters({ type: '', search: '', date_from: '', date_to: '', page: 1 })}
            className="p-1.5 hover:bg-slate-100 rounded" data-testid="tx-clear-filters">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th>S.No.</th><th>Number</th><th>Type</th><th>Voucher</th><th>Party</th>
                  <th>Date</th><th className="text-right">Subtotal</th>
                  <th className="text-right">GST</th><th className="text-right">TDS</th>
                  <th className="text-right">Net Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.transactions.map((tx, idx) => (
                  <tr key={tx.id} className="cursor-pointer hover:bg-slate-50"
                    onClick={() => {
                      if (tx.type === 'invoice') navigate(`/invoices/${tx.id}`);
                      else if (tx.type === 'advance') navigate('/advances');
                      else if (tx.type === 'receipt') navigate(`/receipts/${tx.id}`);
                    }}
                    data-testid={`tx-row-${tx.id}`}>
                    <td className="text-slate-500 text-sm">{(filters.page - 1) * 30 + idx + 1}</td>
                    <td className="font-mono text-sm">{tx.number}</td>
                    <td><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TYPE_COLORS[tx.type] || 'bg-slate-100 text-slate-700'}`}>{TYPE_LABELS[tx.type] || tx.type}</span></td>
                    <td className="text-sm text-slate-600">{VOUCHER_LABELS[tx.voucher_type] || tx.voucher_type}</td>
                    <td className="text-sm">{tx.party}</td>
                    <td className="text-sm text-slate-500">{formatDateDisplay(tx.date)}</td>
                    <td className="text-right font-mono text-sm">Rs.{formatCurrency(tx.subtotal)}</td>
                    <td className="text-right font-mono text-sm text-orange-700">{tx.gst_amount > 0 ? `Rs.${formatCurrency(tx.gst_amount)}` : '-'}</td>
                    <td className="text-right font-mono text-sm text-indigo-700">{tx.tds_amount > 0 ? `Rs.${formatCurrency(tx.tds_amount)}` : '-'}</td>
                    <td className="text-right font-mono text-sm font-medium">Rs.{formatCurrency(tx.net_amount)}</td>
                  </tr>
                ))}
                {!data.transactions.length && (
                  <tr><td colSpan={10} className="text-center text-slate-400 py-8">No transactions found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        {data.total > 30 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <span className="text-sm text-slate-500">Page {filters.page} of {data.pages}</span>
            <div className="flex gap-2">
              <button disabled={filters.page <= 1}
                onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
                className="btn-secondary text-xs">Previous</button>
              <button disabled={filters.page >= data.pages}
                onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
                className="btn-secondary text-xs">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
